package com.zq.webproject.ui.order

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Link
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import com.zq.webproject.utils.UrlLauncher
import kotlinx.coroutines.launch
import com.zq.webproject.getPlatform
import com.zq.webproject.model.OrderDetail
import com.zq.webproject.model.OrderDetailResponse
import com.zq.webproject.network.Repository
import com.zq.webproject.utils.TokenManager
import kotlinx.coroutines.delay


// 图片前缀
private const val IMAGE_PREFIX = "https://image.aiotzq.com/"

private val repository = Repository()

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun OrderDetailScreen(
    orderNo: String,
    onBackClick: () -> Unit,
    onPaymentSuccess: (String, Double, String, String?) -> Unit = { _, _, _, _ -> }  // 支付成功回调：订单号、金额、支付方式、资源链接
) {
    println("OrderDetailScreen - 渲染订单详情页面，订单号: $orderNo") // 日志输出

    // 状态管理
    var isLoading by remember { mutableStateOf(true) }
    var orderDetail by remember { mutableStateOf<OrderDetailResponse?>(null) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var showAlipayDialog by remember { mutableStateOf(false) }
    var alipayQrcodeImageUrl by remember { mutableStateOf<String?>(null) }
    val coroutineScope = rememberCoroutineScope()
    val scrollState = rememberScrollState()

    // 获取Token
    val token = TokenManager.getToken()

    // 请求订单详情接口
    LaunchedEffect(orderNo) {
        println("OrderDetailScreen - 开始请求订单详情，订单号: $orderNo") // 日志输出
        if (token.isNullOrEmpty()) {
            errorMessage = "未登录，请先登录"
            isLoading = false
            println("OrderDetailScreen - Token为空，未登录") // 日志输出
            return@LaunchedEffect
        }

        coroutineScope.launch {
            try {
                val response = repository.getOrderDetail(orderNo, token)
                if (response.isSuccess) {
                    orderDetail = response.getOrNull()
                    println("OrderDetailScreen - 订单详情请求成功: ${orderDetail?.data?.productName}") // 日志输出
                    if (orderDetail?.code != 200) {
                        errorMessage = orderDetail?.message ?: "订单详情获取失败"
                        println("OrderDetailScreen - 订单详情返回错误: $errorMessage") // 日志输出
                    }
                } else {
                    errorMessage = "请求失败：${response.exceptionOrNull()?.message}"
                    println("OrderDetailScreen - 请求失败: $errorMessage") // 日志输出
                }
            } catch (e: Exception) {
                errorMessage = "网络异常：${e.message}"
                println("OrderDetailScreen - 网络异常: ${e.message}") // 日志输出
                e.printStackTrace()
            } finally {
                isLoading = false
            }
        }
    }

    // 主布局
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(text = "订单详情") },
                navigationIcon = {
                    IconButton(onClick = {
                        println("OrderDetailScreen - 点击返回按钮") // 日志输出
                        onBackClick()
                    }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "返回"
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF5F5F5))
        ) {
            when {
                isLoading -> {
                    // 加载中
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                }

                errorMessage != null -> {
                    // 错误页面
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(text = errorMessage!!, color = Color.Red, fontSize = 16.sp)
                            Button(
                                onClick = {
                                    println("OrderDetailScreen - 点击重新加载") // 日志输出
                                    isLoading = true
                                    errorMessage = null
                                    coroutineScope.launch {
                                        try {
                                            val response =
                                                repository.getOrderDetail(orderNo, token!!)
                                            if (response.isSuccess) {
                                                orderDetail = response.getOrNull()
                                                if (orderDetail?.code != 200) {
                                                    errorMessage =
                                                        orderDetail?.message ?: "订单详情获取失败"
                                                }
                                            } else {
                                                errorMessage =
                                                    "请求失败：${response.exceptionOrNull()?.message}"
                                            }
                                        } catch (e: Exception) {
                                            errorMessage = "网络异常：${e.message}"
                                        } finally {
                                            isLoading = false
                                        }
                                    }
                                },
                                modifier = Modifier.padding(top = 16.dp)
                            ) {
                                Text(text = "重新加载")
                            }
                        }
                    }
                }

                orderDetail?.data == null -> {
                    // 订单不存在
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(text = "订单不存在", fontSize = 18.sp, color = Color.Gray)
                    }
                }

                else -> {
                    val order = orderDetail!!.data!!
                    println("OrderDetailScreen - 开始渲染订单详情内容: ${order.productName}") // 日志输出

                    // 支付方式选择状态：0=余额支付，2=支付宝支付
                    var selectedPaymentMethod by remember { mutableStateOf(0) }

                    Box(modifier = Modifier.fillMaxSize()) {
                        // 订单详情内容（包含支付方式选择）
                        Column(modifier = Modifier.fillMaxSize().verticalScroll(scrollState)) {
                            // 1. 订单状态卡片
                            OrderStatusCard(order)

                            Spacer(modifier = Modifier.height(8.dp))

                            // 2. 商品信息卡片
                            ProductInfoCard(order)

                            Spacer(modifier = Modifier.height(8.dp))

                            // 3. 订单信息卡片
                            OrderInfoCard(order)

                            // 3.5 已支付订单显示资源链接按钮（只有当productImage3有值时才显示）
                            if (order.payStatus == 1 && !order.productImage3.isNullOrEmpty()) {
                                Spacer(modifier = Modifier.height(8.dp))
                                ResourceLinkCard(order.productImage3!!)
                            }

                            // 4. 支付方式选择区域（待支付时显示）
                            if (order.payStatus == 0) {
                                Spacer(modifier = Modifier.height(8.dp))
                                PaymentMethodSection(
                                    orderNo = order.orderNo,
                                    selectedPaymentMethod = selectedPaymentMethod,
                                    onPaymentMethodChange = { selectedPaymentMethod = it }
                                )
                            }

                            // 底部留出空间给悬浮按钮
                            Spacer(modifier = Modifier.height(if (order.payStatus == 0) 80.dp else 32.dp))
                        }

                        // 5. 底部悬浮支付按钮（待支付时显示）
                        if (order.payStatus == 0 && order.productPrice != null && order.productId != null) {
                            BottomPaymentBar(
                                price = order.productPrice!!,
                                orderNo = order.orderNo,
                                productId = order.productId!!,
                                productImage3 = order.productImage3,
                                selectedPaymentMethod = selectedPaymentMethod,
                                onPaymentSuccess = onPaymentSuccess,
                                onShowAlipayDialog = { qrcodeImageUrl ->
                                    alipayQrcodeImageUrl = qrcodeImageUrl
                                    showAlipayDialog = true
                                }
                            )
                        }
                    }

                    // 支付宝支付对话框
                    if (showAlipayDialog && alipayQrcodeImageUrl != null) {
                        AlipayQrcodeDialog(
                            qrcodeUrl = alipayQrcodeImageUrl!!,
                            orderNo = orderNo,
                            onDismiss = {
                                showAlipayDialog = false
                                alipayQrcodeImageUrl = null
                            },
                            onPaymentSuccess = {
                                showAlipayDialog = false
                                alipayQrcodeImageUrl = null
                                onPaymentSuccess(orderNo, order.productPrice ?: 0.0, "alipay", order.productImage3)  // 支付宝支付，传递资源链接
                            }
                        )
                    }
                }
            }
        }
    }
}

// 1. 订单状态卡片
@Composable
fun OrderStatusCard(order: OrderDetail) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = if (order.payStatus == 0) "待支付" else "已支付",
                fontSize = 24.sp,
                fontWeight = FontWeight.Bold,
                color = if (order.payStatus == 0) Color(0xFFFF6B6B) else Color(0xFF4CAF50)
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = "订单号: ${order.orderNo}",
                fontSize = 14.sp,
                color = Color.Gray
            )
            Text(
                text = "下单时间: ${order.orderTime ?: ""}",
                fontSize = 14.sp,
                color = Color.Gray
            )
        }
    }
}

// 2. 商品信息卡片
@Composable
fun ProductInfoCard(order: OrderDetail) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
            Text(
                text = "商品信息",
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.padding(bottom = 12.dp)
            )

            Row(modifier = Modifier.fillMaxWidth()) {
                // 商品图片
                AsyncImage(
                    model = if (order.productImage?.startsWith("http") == true)
                        order.productImage
                    else
                        IMAGE_PREFIX + (order.productImage ?: ""),
                    contentDescription = "商品图片",
                    modifier = Modifier
                        .size(100.dp)
                        .background(Color(0xFFF5F5F5), RoundedCornerShape(8.dp))
                )

                Spacer(modifier = Modifier.width(12.dp))

                // 商品信息
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = order.productName ?: "",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Medium,
                        maxLines = 2
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = "分类: ${order.productCategory}",
                        fontSize = 14.sp,
                        color = Color.Gray
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Row {
                        Text(text = "¥", fontSize = 14.sp, color = Color.Red)
                        Text(
                            text = order.productPrice.toString(),
                            fontSize = 20.sp,
                            color = Color.Red,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }
        }
    }
}

// 3. 订单信息卡片
@Composable
fun OrderInfoCard(order: OrderDetail) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
            Text(
                text = "订单信息",
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.padding(bottom = 12.dp)
            )

            OrderInfoRow("订单编号", order.orderNo)
            OrderInfoRow("下单时间", order.orderTime ?: "")
            OrderInfoRow("商品金额", "¥${order.productPrice ?: 0.0}")
            OrderInfoRow("实付金额", "¥${order.productPrice ?: 0.0}")
            OrderInfoRow(
                "支付状态",
                if (order.payStatus == 0) "待支付" else "已支付",
                if (order.payStatus == 0) Color(0xFFFF6B6B) else Color(0xFF4CAF50)
            )
        }
    }
}

@Composable
fun OrderInfoRow(label: String, value: String, valueColor: Color = Color.Black) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(text = label, fontSize = 14.sp, color = Color.Gray)
        Text(text = value, fontSize = 14.sp, color = valueColor, fontWeight = FontWeight.Medium)
    }
}

// 4. 支付方式选择区域
@Composable
fun PaymentMethodSection(
    orderNo: String,
    selectedPaymentMethod: Int,
    onPaymentMethodChange: (Int) -> Unit
) {
    println("OrderDetailScreen - 渲染支付方式选择区域，订单号: $orderNo, 当前选择: $selectedPaymentMethod") // 日志输出

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            // 标题
            Text(
                text = "支付方式",
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.padding(bottom = 12.dp)
            )

            // 余额支付
            PaymentMethodItem(
                title = "余额支付",
                subtitle = "使用账户余额支付",
                isSelected = selectedPaymentMethod == 0,
                onClick = {
                    println("OrderDetailScreen - 选择余额支付") // 日志输出
                    onPaymentMethodChange(0)
                }
            )

            Spacer(modifier = Modifier.height(8.dp))

            // 支付宝支付
            PaymentMethodItem(
                title = "支付宝支付",
                subtitle = "使用支付宝扫码支付",
                isSelected = selectedPaymentMethod == 2,
                onClick = {
                    println("OrderDetailScreen - 选择支付宝支付") // 日志输出
                    onPaymentMethodChange(2)
                }
            )
        }
    }
}

// 5. 底部悬浮支付栏
@Composable
fun BoxScope.BottomPaymentBar(
    price: Double,
    orderNo: String,
    productId: Int,
    productImage3: String?,
    selectedPaymentMethod: Int,
    onPaymentSuccess: (String, Double, String, String?) -> Unit,  // 订单号、金额、支付方式、资源链接
    onShowAlipayDialog: (String) -> Unit
) {
    println("OrderDetailScreen - 渲染底部支付栏，订单号: $orderNo, 金额: $price, 支付方式: $selectedPaymentMethod") // 日志输出

    var isPaying by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    val coroutineScope = rememberCoroutineScope()
    val token = TokenManager.getToken()

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .align(Alignment.BottomCenter)
            .background(Color.White)
            .padding(horizontal = 16.dp, vertical = 12.dp)
    ) {
        // 错误提示（显示在支付栏上方）
        if (errorMessage != null) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFFFEBEE), RoundedCornerShape(8.dp))
                    .padding(12.dp)
            ) {
                Text(
                    text = errorMessage!!,
                    color = Color(0xFFD32F2F),
                    fontSize = 14.sp
                )
            }
            Spacer(modifier = Modifier.height(12.dp))
        }

        // 支付按钮和金额
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column {
                Text(text = "实付金额", fontSize = 12.sp, color = Color.Gray)
                Row {
                    Text(text = "¥", fontSize = 16.sp, color = Color.Red)
                    Text(
                        text = price.toString(),
                        fontSize = 24.sp,
                        color = Color.Red,
                        fontWeight = FontWeight.Bold
                    )
                }
            }

            Button(
                onClick = {
                    println("OrderDetailScreen - 点击立即支付按钮，支付方式: $selectedPaymentMethod") // 日志输出

                    if (token.isNullOrEmpty()) {
                        errorMessage = "未登录，请先登录"
                        return@Button
                    }

                    isPaying = true
                    errorMessage = null

                    coroutineScope.launch {
                        try {
                            when (selectedPaymentMethod) {
                                0 -> {
                                    // 余额支付
                                    println("OrderDetailScreen - 开始余额支付，订单号: $orderNo, 金额: $price") // 日志输出
                                    val response = repository.payWithBalance(price, orderNo, token)

                                    if (response.isSuccess) {
                                        val paymentResponse = response.getOrNull()
                                        if (paymentResponse?.code == 200) {
                                            println("OrderDetailScreen - 余额支付成功") // 日志输出
                                            onPaymentSuccess(orderNo, price, "balance", productImage3)  // 余额支付，传递资源链接
                                        } else {
                                            errorMessage = paymentResponse?.message ?: "支付失败"
                                            println("OrderDetailScreen - 余额支付失败: $errorMessage") // 日志输出

                                            // 如果是订单已支付，提示用户刷新页面
                                            if (errorMessage?.contains("已支付") == true) {
                                                errorMessage =
                                                    "$errorMessage，请返回后重新进入订单详情"
                                            }
                                        }
                                    } else {
                                        errorMessage =
                                            "支付请求失败：${response.exceptionOrNull()?.message}"
                                        println("OrderDetailScreen - 余额支付请求失败: $errorMessage") // 日志输出
                                    }
                                }

                                2 -> {
                                    // 支付宝支付
                                    println("OrderDetailScreen - 开始支付宝支付，商品ID: $productId") // 日志输出
                                    val response = repository.createAlipayOrder(
                                        productId = productId,
                                        paymentType = "alipay",
                                        quantity = 1,
                                        token = token
                                    )

                                    if (response.isSuccess) {
                                        val alipayResponse = response.getOrNull()
                                        if (alipayResponse != null) {
                                            // 支持两种响应格式：
                                            // 1. 标准RESTful格式: code=200, data={qrcode: "..."}
                                            // 2. 易支付格式: code=1, qrcode="..."
                                            val isSuccess =
                                                (alipayResponse.code == 200 && alipayResponse.data != null) ||
                                                        (alipayResponse.code == 1)
                                            val qrcodeUrl =
                                                alipayResponse.data?.qrcode ?: alipayResponse.qrcode
                                            val payurl =
                                                alipayResponse.data?.payurl ?: alipayResponse.payurl
                                            val qrcodeImageUrl =
                                                alipayResponse.data?.img ?: alipayResponse.img

                                            if (isSuccess && (qrcodeUrl != null || qrcodeImageUrl != null)) {
                                                println("OrderDetailScreen - 支付宝订单创建成功") // 日志输出
                                                println("OrderDetailScreen - 支付链接: $qrcodeUrl") // 日志输出
                                                println("OrderDetailScreen - 二维码图片: $qrcodeImageUrl") // 日志输出
                                                
                                                // 获取支付宝订单号（用于轮询查询）
                                                val alipayOrderNo = alipayResponse.data?.orderNo
                                                println("OrderDetailScreen - 支付宝订单号: $alipayOrderNo") // 日志输出

                                                // 判断平台：Android/iOS直接打开支付宝APP，Web显示二维码
                                                val platform = getPlatform().name
                                                if ((platform.contains(
                                                        "Android",
                                                        ignoreCase = true
                                                    ) || platform.contains(
                                                        "iOS",
                                                        ignoreCase = true
                                                    )) && payurl != null
                                                ) {
                                                    // Android/iOS平台：检查payurl是否为支付宝URL Scheme
                                                    if (payurl.startsWith("alipay://", ignoreCase = true) || 
                                                        payurl.startsWith("alipays://", ignoreCase = true)) {
                                                        // 直接调起支付宝APP
                                                        println("OrderDetailScreen - ${platform}平台，直接打开支付宝APP: $payurl") // 日志输出
                                                        UrlLauncher.openUrl(payurl)
                                                        errorMessage = "已打开支付宝APP，支付完成后请返回查看订单状态"
                                                    } else {
                                                        // payurl是HTTPS链接，在浏览器打开支付页面
                                                        println("OrderDetailScreen - ${platform}平台，在浏览器打开支付页面: $payurl") // 日志输出
                                                        UrlLauncher.openUrl(payurl)
                                                        errorMessage = "已在浏览器打开支付页面，支付完成后请返回"
                                                    }
                                                    // 提示用户支付完成后返回
                                                    
                                                    // 启动后台轮询检查支付状态（Android/iOS平台）
                                                    // 注意：使用支付宝订单号进行查询！
                                                    if (alipayOrderNo != null) {
                                                        println("OrderDetailScreen - [$platform] 启动支付状态轮询检查，使用支付宝订单号: $alipayOrderNo")
                                                        coroutineScope.launch {
                                                            try {
                                                                // 等待5秒后开始检查（给用户时间完成支付）
                                                                delay(5000)
                                                                
                                                                // 轮询检查支付状态（最多检查10次，每次间隔3秒）
                                                                var retryCount = 0
                                                                val maxRetries = 10
                                                                var paymentVerified = false
                                                                
                                                                while (retryCount < maxRetries && !paymentVerified) {
                                                                    if (retryCount > 0) {
                                                                        println("OrderDetailScreen - 第${retryCount + 1}次查询支付状态，等待3秒...")
                                                                        delay(3000)
                                                                    }
                                                                    
                                                                    // 使用支付宝订单号查询！
                                                                    println("OrderDetailScreen - 检查支付状态，支付宝订单号: $alipayOrderNo (第${retryCount + 1}次)")
                                                                    val checkResponse = repository.getOrderDetail(alipayOrderNo, token)
                                                                    
                                                                    if (checkResponse.isSuccess) {
                                                                        val checkOrderDetail = checkResponse.getOrNull()
                                                                        if (checkOrderDetail?.code == 200 && checkOrderDetail.data != null) {
                                                                            val payStatus = checkOrderDetail.data.payStatus
                                                                            println("OrderDetailScreen - 当前支付状态: $payStatus (0=未支付, 1=已支付)")
                                                                            
                                                                            if (payStatus == 1) {
                                                                                // 支付成功
                                                                                println("OrderDetailScreen - 🎉 支付成功！订单已支付")
                                                                                paymentVerified = true
                                                                                errorMessage = null
                                                                                // 调用支付成功回调，跳转到成功页面（使用原始订单号）
                                                                                onPaymentSuccess(orderNo, price, "alipay", productImage3)  // 支付宝支付，传递资源链接
                                                                            } else {
                                                                                // 未支付，继续重试
                                                                                retryCount++
                                                                            }
                                                                        } else {
                                                                            println("OrderDetailScreen - 查询订单失败: ${checkOrderDetail?.message}")
                                                                            retryCount++
                                                                        }
                                                                    } else {
                                                                        println("OrderDetailScreen - 网络请求失败: ${checkResponse.exceptionOrNull()?.message}")
                                                                        retryCount++
                                                                    }
                                                                }
                                                                
                                                                if (!paymentVerified) {
                                                                    println("OrderDetailScreen - 未检测到支付，请手动刷新订单状态")
                                                                    errorMessage = "未检测到支付，如已支付请手动刷新订单"
                                                                }
                                                            } catch (e: Exception) {
                                                                println("OrderDetailScreen - 支付状态检查异常: ${e.message}")
                                                                e.printStackTrace()
                                                            }
                                                        }
                                                    } else {
                                                        println("OrderDetailScreen - 未获取到支付宝订单号，无法轮询")
                                                        errorMessage = "支付宝订单创建成功，但无法自动检测支付状态"
                                                    }
                                                } else if (qrcodeImageUrl != null) {
                                                    // Web平台：显示二维码对话框
                                                    println("OrderDetailScreen - Web平台，显示二维码对话框") // 日志输出
                                                    println("OrderDetailScreen - 原始二维码图片URL: $qrcodeImageUrl") // 日志输出
                                                    
                                                    // 使用后端代理接口（避免跨域）
                                                    val proxiedQrcodeUrl = "https://shop.aiotzq.com/api/payment/proxy-image?url=${qrcodeImageUrl}"
                                                    println("OrderDetailScreen - 代理后的二维码URL: $proxiedQrcodeUrl") // 日志输出
                                                    
                                                    // 调用回调显示二维码弹窗
                                                    onShowAlipayDialog(proxiedQrcodeUrl)
                                                } else {
                                                    // 降级方案：使用支付链接
                                                    println("OrderDetailScreen - 降级方案，打开支付链接") // 日志输出
                                                    if (qrcodeUrl != null) {
                                                        UrlLauncher.openUrl(qrcodeUrl)
                                                        errorMessage =
                                                            "支付链接已在新标签页打开，请使用支付宝扫码或登录支付。"
                                                    } else {
                                                        errorMessage = "未获取到支付信息，请稍后重试"
                                                    }
                                                }
                                            } else {
                                                val errorMsg =
                                                    alipayResponse.message ?: alipayResponse.msg
                                                    ?: "支付宝订单创建失败"
                                                errorMessage = errorMsg
                                                println("OrderDetailScreen - 支付宝订单创建失败: $errorMsg") // 日志输出
                                            }
                                        } else {
                                            errorMessage = "支付宝响应为空"
                                            println("OrderDetailScreen - 支付宝响应为空") // 日志输出
                                        }
                                    } else {
                                        errorMessage =
                                            "支付宝请求失败：${response.exceptionOrNull()?.message}"
                                        println("OrderDetailScreen - 支付宝请求失败: $errorMessage") // 日志输出
                                    }
                                }
                            }
                        } catch (e: Exception) {
                            errorMessage = "支付异常：${e.message}"
                            println("OrderDetailScreen - 支付异常: ${e.message}") // 日志输出
                            e.printStackTrace()
                        } finally {
                            isPaying = false
                        }
                    }
                },
                enabled = !isPaying,
                modifier = Modifier.width(120.dp).height(48.dp),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFFF6B6B)),
                shape = RoundedCornerShape(24.dp)
            ) {
                if (isPaying) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text(text = "立即支付", fontSize = 16.sp, fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}

// 支付方式选项
@Composable
fun PaymentMethodItem(
    title: String,
    subtitle: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = if (isSelected) 2.dp else 1.dp,
                color = if (isSelected) Color(0xFF1E88E5) else Color(0xFFEEEEEE),
                shape = RoundedCornerShape(8.dp)
            )
            .background(
                color = if (isSelected) Color(0xFFE3F2FD) else Color.White,
                shape = RoundedCornerShape(8.dp)
            )
            .clickable(onClick = onClick)
            .padding(16.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Column {
            Text(
                text = title,
                fontSize = 16.sp,
                fontWeight = FontWeight.Medium,
                color = if (isSelected) Color(0xFF1E88E5) else Color.Black
            )
            Text(
                text = subtitle,
                fontSize = 12.sp,
                color = Color.Gray,
                modifier = Modifier.padding(top = 4.dp)
            )
        }

        RadioButton(
            selected = isSelected,
            onClick = onClick,
            colors = RadioButtonDefaults.colors(
                selectedColor = Color(0xFF1E88E5)
            )
        )
    }
}

// 资源链接卡片（已支付订单显示）
@Composable
fun ResourceLinkCard(resourceUrl: String) {
    println("OrderDetailScreen - 渲染资源链接卡片，链接: $resourceUrl") // 日志输出

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            Text(
                text = "资源链接",
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.padding(bottom = 12.dp)
            )

            OutlinedButton(
                onClick = {
                    println("OrderDetailScreen - 点击查看资源链接: $resourceUrl") // 日志输出
                    UrlLauncher.openUrl(resourceUrl)
                },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(8.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = Color(0xFF1E88E5)
                )
            ) {
                Icon(
                    imageVector = Icons.Default.Link,
                    contentDescription = "链接",
                    modifier = Modifier.size(20.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = "查看资源链接",
                    fontSize = 16.sp,
                    fontWeight = FontWeight.Medium
                )
            }
        }
    }
}

// 支付宝二维码对话框
@Composable
fun AlipayQrcodeDialog(
    qrcodeUrl: String,
    orderNo: String,
    onDismiss: () -> Unit,
    onPaymentSuccess: () -> Unit
) {
    println("OrderDetailScreen - 显示支付宝二维码对话框: $qrcodeUrl") // 日志输出

    var isChecking by remember { mutableStateOf(false) }
    var checkErrorMessage by remember { mutableStateOf<String?>(null) }
    val coroutineScope = rememberCoroutineScope()
    val token = TokenManager.getToken()

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = "支付宝扫码支付",
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold
            )
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = "请使用支付宝扫描下方二维码完成支付",
                    fontSize = 14.sp,
                    color = Color.Gray,
                    modifier = Modifier.padding(bottom = 16.dp)
                )

                // 二维码图片 - 使用iframe绕过CORS
                Box(
                    modifier = Modifier
                        .size(300.dp)
                        .background(Color.White, RoundedCornerShape(8.dp))
                        .padding(8.dp),
                    contentAlignment = Alignment.Center
                ) {
                    // 使用HTML iframe来显示二维码（绕过CORS限制）
                    QrcodeImageView(qrcodeUrl)
                }

                Spacer(modifier = Modifier.height(16.dp))

                Text(
                    text = "支付完成后，请点击下方按钮验证支付状态",
                    fontSize = 12.sp,
                    color = Color.Gray
                )

                // 显示验证错误信息
                if (checkErrorMessage != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = checkErrorMessage!!,
                        fontSize = 12.sp,
                        color = Color.Red,
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))

                // 提供备用链接
                TextButton(
                    onClick = {
                        println("OrderDetailScreen - 在浏览器中打开二维码")
                        UrlLauncher.openUrl(qrcodeUrl)
                    }
                ) {
                    Text(
                        text = "在新窗口中打开",
                        fontSize = 12.sp,
                        color = Color(0xFF1E88E5)
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    println("OrderDetailScreen - 用户点击'我已支付'按钮")

                    if (token.isNullOrEmpty()) {
                        checkErrorMessage = "未登录，无法验证支付状态"
                        return@Button
                    }

                    isChecking = true
                    checkErrorMessage = null

                    coroutineScope.launch {
                        try {
                            // 轮询查询支付状态（最多3次，每次间隔2秒）
                            var retryCount = 0
                            val maxRetries = 3
                            var paymentVerified = false

                            while (retryCount < maxRetries && !paymentVerified) {
                                if (retryCount > 0) {
                                    println("OrderDetailScreen - 第${retryCount + 1}次查询支付状态，等待2秒...")
                                    delay(2000) // 等待2秒
                                }

                                println("OrderDetailScreen - 开始验证支付状态，订单号: $orderNo (第${retryCount + 1}次)")
                                val response = repository.getOrderDetail(orderNo, token)

                                if (response.isSuccess) {
                                    val orderDetail = response.getOrNull()
                                    if (orderDetail?.code == 200 && orderDetail.data != null) {
                                        val payStatus = orderDetail.data.payStatus
                                        println("OrderDetailScreen - 查询到支付状态: $payStatus (0=未支付, 1=已支付)")

                                        if (payStatus == 1) {
                                            // 支付成功
                                            println("OrderDetailScreen - 支付验证成功，订单已支付")
                                            paymentVerified = true
                                            onPaymentSuccess()
                                        } else {
                                            // 未支付，继续重试
                                            retryCount++
                                            if (retryCount < maxRetries) {
                                                checkErrorMessage =
                                                    "支付确认中，请稍候...(${retryCount}/${maxRetries})"
                                            } else {
                                                println("OrderDetailScreen - 支付验证失败，订单尚未支付")
                                                checkErrorMessage =
                                                    "订单尚未支付。如果您已支付，请等待10-30秒后再次点击验证，或联系客服处理。"
                                            }
                                        }
                                    } else {
                                        checkErrorMessage = orderDetail?.message ?: "查询订单失败"
                                        println("OrderDetailScreen - 查询订单失败: $checkErrorMessage")
                                        break
                                    }
                                } else {
                                    checkErrorMessage = "网络请求失败，请重试"
                                    println("OrderDetailScreen - 网络请求失败: ${response.exceptionOrNull()?.message}")
                                    break
                                }
                            }
                        } catch (e: Exception) {
                            checkErrorMessage = "验证失败：${e.message}"
                            println("OrderDetailScreen - 验证异常: ${e.message}")
                            e.printStackTrace()
                        } finally {
                            isChecking = false
                        }
                    }
                },
                enabled = !isChecking,
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color(0xFF1E88E5)
                )
            ) {
                if (isChecking) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text("我已支付")
                }
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("取消支付")
            }
        },
        containerColor = Color.White,
        shape = RoundedCornerShape(16.dp)
    )
}

// 二维码图片视图（平台特定实现）
@Composable
expect fun QrcodeImageView(imageUrl: String)